import React, { useMemo } from "react";
import { sanitizePassagemUserCopy } from "../../pages/Passagens/passagemDisplayCopy";
import { Box, Typography, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";

function dotColor(kind) {
  if (kind === "payment") return "secondary.main";
  if (kind === "funnel") return "primary.main";
  if (kind === "bpe") return "success.main";
  if (kind === "legacy") return "info.main";
  return "grey.500";
}

function formatAt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

function eventLabel(ev, t) {
  if (ev.kind === "funnel") {
    const step =
      ev.stepLabel ||
      t(`passagensPage.journeyStep_${ev.step}`, { defaultValue: ev.step });
    return ev.sourceLabel ? `${step} (${ev.sourceLabel})` : step;
  }
  if (ev.stepLabel) return sanitizePassagemUserCopy(ev.message || ev.stepLabel);
  return sanitizePassagemUserCopy(ev.message || ev.step || "—");
}

/** Timeline da jornada de compra (funil, legado, BPe, pagamentos). */
export default function PassagemJourneyTimeline({ events = [], funnel = [], audit = [] }) {
  const { t } = useTranslation();

  const timeline = useMemo(() => {
    if (events?.length) return events;
    const legacy = [];
    for (const f of funnel || []) {
      legacy.push({
        kind: "funnel",
        step: f.step,
        stepLabel: f.step,
        sourceLabel: f.source,
        at: f.created_at,
      });
    }
    for (const a of audit || []) {
      legacy.push({ kind: "audit", message: a.mensagem, at: a.data_cadastro });
    }
    return legacy;
  }, [events, funnel, audit]);

  if (!timeline.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
        {t("passagensPage.journeyEmpty", { defaultValue: "Nenhuma etapa registrada para esta venda." })}
      </Typography>
    );
  }

  return (
    <Stack spacing={0} sx={{ pl: 0.5 }}>
      {timeline.map((ev, i) => (
        <Stack key={`${ev.at}-${ev.kind}-${i}`} direction="row" spacing={1.5} alignItems="stretch">
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: 14,
              pt: 0.75,
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                border: 2,
                borderColor: dotColor(ev.kind),
                bgcolor: "background.paper",
                flexShrink: 0,
              }}
            />
            {i < timeline.length - 1 && (
              <Box sx={{ width: 2, flex: 1, minHeight: 20, bgcolor: "divider", my: 0.5 }} />
            )}
          </Box>
          <Box sx={{ pb: i < timeline.length - 1 ? 1.5 : 0, flex: 1, minWidth: 0 }}>
            <Typography variant="body2">{eventLabel(ev, t)}</Typography>
            <Typography variant="caption" color="text.secondary">
              {formatAt(ev.at)}
            </Typography>
          </Box>
        </Stack>
      ))}
    </Stack>
  );
}
